import utils, {deepCase} from '@imt/vue-toolbox/src/utils';
import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';

import {authedAxios, inAdmin} from '@/store';
import {resolveRoleForAgency, inTestMode} from '@/utils';

const policyDataIncludes = {
        types: {
            accommodations: 'accommodation_history.user',
            accommodation_history: '',
            attachments: '',
            agent_remarks: '',
            company: '',
            uassists: '',
            buildings: '',
            class_codes: 'premium',
            drivers: 'entity',
            forms: 'premium',
            individuals: 'entity',
            locations: 'address',
            losses: '',
            premium: '',
            policy: '',
            policy_group_snapshot: 'agency,policy_group_snapshot.mailing_address,policy_group_snapshot.named_insureds.entities,' +
                'policy_group_snapshot.insured_contact,policy_group_snapshot.inspection_contact,policy_group_snapshot.premium_audit_contact',
            tax_ids: 'named_insured.entities',
        },
        includeNames: {
            named_insureds: ['entities']
        },
        relationshipNames: {
            named_insureds: ['entities']
        }
    },
    policyDataUrlType = {
        'Accommodation': 'accommodations',
        'AccommodationHistory': 'accommodation-history',
        'Activity': 'activities',
        'Attachment': 'attachments',
        'Building': 'buildings',
        'Comment': 'comments',
        'Driver': 'drivers',
        'Form': 'forms',
        'ClassCode': 'class-codes',
        'Location': 'locations',
        'Individual': 'individuals',
        'Loss': 'losses',
        'Address': 'addresses',
        'AddressInfo': 'address-info',
        'NamedInsured': 'named-insureds',
        'Policy': 'policies',
        'Version': 'versions',
        'Entity': 'entities',
        'Uassist': 'uassists',
        'TaxId': 'tax-ids',
    };

export const RULES_CATALOG_MAX_AGE_MS = 60 * 1000;

export function getPolicyVersionIncludes() {
    let includes = Object.keys(policyDataIncludes.types).map(k => policyDataIncludes.types[k] ? `${k}.${policyDataIncludes.types[k]}` : k),
        adminFields = {
            forms: [
                'attachment',
                'csr_help_wording',
                'help_wording',
                'form_type',
                'screen_name',
                'state',
                'user_availability',
                'web_form',
            ],
            class_codes: [
                'code',
                'csr_only',
                'csr_help_wording',
                'help_wording',
                'state',
                'title',
                'web_form',
            ],
        };

    for (const [resourceType, fields] of Object.entries(adminFields)) {
        includes += `&fields[${resourceType}]=${fields.join(',')}`;
    }

    return includes;
}

function groupRulesByType(rules) {
    return rules.reduce((acc, rule) => {
        (acc[camelCase(rule.ruleType)] ??= []).push(rule);

        return acc;
    }, {});
}

function permissionResolver(state, rootState) {
    const roles = rootState.toolbox.user?.roles;

    // Roles scope a user to an agency, so only the role for this policy's agency can grant access.
    return (permission) => {
        if (roles?.length) {
            return !!resolveRoleForAgency(roles, state.policyGroup?.agency?.id)?.permissions?.includes(permission);
        }

        return !!rootState.toolbox.permissions[permission];
    };
}

export const getters = {
    userHasEditPermissions: (state, _getters, rootState) => {
        const permissions = rootState.toolbox.permissions;

        if (!permissions) {
            return false;
        }

        // Roles never carry policy_system_internal, derive it from the direct permission
        const policySystemInternal = !!permissions.policy_system_internal;

        if (inTestMode() && policySystemInternal) {
            return true;
        }

        const hasPermission = permissionResolver(state, rootState);

        if (permissions.commercial_system_access
            && !hasPermission('commercial_full_access')
            && !hasPermission('commercial_quote_access')) {
            return false;
        }

        if (state.policyVersion.processingStatus === 'rejected') {
            return policySystemInternal;
        }

        if (['amendment', 'renewal'].includes(state.policyVersion?.transactionType)) {
            return hasPermission('commercial_full_access');
        }

        return hasPermission('commercial_full_access') || hasPermission('commercial_quote_access');
    },
    userHasPagePermission: (state, _getters, rootState) => (pageType) => {
        const hasPermission = permissionResolver(state, rootState),
            // Roles never carry policy_system_internal, derive it from the direct permission
            policySystemInternal = !!rootState.toolbox.permissions.policy_system_internal;

        if (inTestMode() && policySystemInternal) {
            return true;
        }

        if (pageType === 'submission') {
            return hasPermission('commercial_full_access');
        }

        if (pageType === 'quote') {
            if (state.policyVersion.processingStatus === 'rejected'
                && !state.policyVersion.extension.system_reject) {
                return hasPermission('policy_system_internal');
            }
        }

        return true;
    },
    policyDataSet(state) {
        // Remove relation keys to avoid max call stack errors when evaluating rules
        return {
            'policyGroup': {...state.policyGroup, policies: undefined, policyGroup: undefined},
            'policy': {...state.policy, group: undefined, versions: undefined},
            'version': {...state.policyVersion, policy: undefined, policyGroupSnapshot: undefined, priorVersion: undefined},
            'priorVersion': {...state.priorVersion, policy: undefined, policyGroupSnapshot: undefined, priorVersion: undefined}
        };
    },
    policyVersionIsQuoted(state) {
        return !!state.policyVersion?.premium?.id;
    },
    policyVersionIsEditable(state, _getters) {
        if (!state.policy.id || !state.policyVersion.id) {
            return false;
        }

        const isEditableStatus = ['in_progress', 'pending', 'rejected', 'temporary'].includes(state.policyVersion.processingStatus);

        if (!isEditableStatus) {
            return false;
        }

        return _getters.userHasEditPermissions;
    },
};

export const actions = {
    async cleanupPolicy({commit, state}, {versionId, onSubmission}) {
        let queryParams = '';

        if (state.debugMode) {
            queryParams += '&debug_mode=true';
        }

        if (onSubmission) {
            queryParams += '&on_submission=true';
        }

        const response = await authedAxios.post(
            `${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/versions/${versionId}/cleanup/?include=${getPolicyVersionIncludes()}${queryParams}`,
            utils.dataFormatter.serialize({stuff: {
                id: versionId,
                type: 'Version'
            }})
        );

        commit('SET_POLICY_SYSTEM_DATA', {type: 'policyVersion', data: deepCase(utils.dataFormatter.deserialize(response.data), 'camel', ['extension', 'dynamicOptions'])});
    },
    async scrubAddress(_, data) {
        const url = `${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/addresses/scrub/`,
            response = await authedAxios.post(url, utils.dataFormatter.serialize({stuff: data}));

        return response.data.data;
    },
    async deletePolicyResource(_, {id, type, versionId}) {
        await authedAxios.delete(`${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/versions/${versionId}/${policyDataUrlType[type]}/${id}/`);
    },
    async fetchCompany(_, {id}) {
        const response = await authedAxios.get(`${process.env.VUE_APP_ADMIN_URL_PLATFORM_ADMIN}/api/v1/companies/${id}/`);

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchJob(_, {jobId}) {
        const url = inAdmin()
            ? `${process.env.VUE_APP_BEHANDLE_URL}/api/v1/jobs/${jobId}/`
            : `${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/behandle/jobs/${jobId}/`;

        let response = await authedAxios.get(url);

        return utils.dataFormatter.deserialize(response.data);
    },
    async fetchPolicy({commit}, policyId) {
        const response = await authedAxios.get(`${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/policies/${policyId}/?include=versions,policy_comments,policy_comments.created_by`),
            policy = utils.dataFormatter.deserialize(response.data);

        commit('SET_POLICY_SYSTEM_DATA', {type: 'policy', data: policy});

        return policy;
    },
    async fetchPolicyGroupForVersion({commit}, versionId) {
        const response = await authedAxios.get(`${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/versions/${versionId}/group/?include=agency,mailing_address,named_insureds.entities`),
            policyGroup = deepCase(utils.dataFormatter.deserialize(response.data), 'camel', ['extension', 'dynamicOptions']);

        commit('SET_POLICY_SYSTEM_DATA', {type: 'policyGroup', data: policyGroup});

        return policyGroup;
    },
    async fetchPolicyNumber(context, prefix) {
        const response = await authedAxios.get(`${process.env.VUE_APP_NUMBERS_URL}/api/v1/counter/${prefix}/`);

        return utils.dataFormatter.deserialize(response.data).number;
    },
    async setPolicyData({ state, commit }, policyVersion) {
        let priorVersion = state.priorVersion;

        if (policyVersion.priorVersionId && Object.keys(priorVersion).length === 0) {
            const priorResponse = await authedAxios.get(
                `${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/versions/${policyVersion.priorVersionId}/?include=${getPolicyVersionIncludes()}`
            );
            priorVersion = deepCase(utils.dataFormatter.deserialize(priorResponse.data), 'camel', ['extension', 'dynamicOptions']);
        }

        commit('SET_POLICY_SYSTEM_DATA', {
            type: 'policyGroup',
            data: {
                ...policyVersion.policyGroupSnapshot,
                id: policyVersion.policyGroupSnapshot.policyGroupId,
                type: 'PolicyGroup'
            }
        });

        commit('SET_POLICY_SYSTEM_DATA', { type: 'policy', data: policyVersion.policy });
        commit('SET_POLICY_SYSTEM_DATA', { type: 'policyVersion', data: policyVersion });
        commit('SET_POLICY_SYSTEM_DATA', { type: 'priorVersion', data: priorVersion });

        return policyVersion;
    },
    async fetchPolicyVersion(context, { versionId }) {
        const response = await authedAxios.get(
                `${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/versions/${versionId}/?include=${getPolicyVersionIncludes()}`
            ),

            policyVersion = deepCase(utils.dataFormatter.deserialize(response.data), 'camel', ['extension', 'dynamicOptions']);

        return await context.dispatch('setPolicyData', policyVersion);
    },
    async fetchProcess(_, {code, policySystemId}) {
        let response = await authedAxios.get(`${process.env.VUE_APP_BEHANDLE_URL}/api/v1/processes/?filter[code]=${code}&filter[policy_system_id]=${policySystemId || null}`);

        return utils.dataFormatter.deserialize(response.data);
    },
    /**
     * `rulesFastMode` (PSPolicySystem with `psfe_fast_mode`): one rules GET per policy context, catalog + `APPLY_RULES_FOR_PAGE`.
     * The catalog GET intentionally omits `page_id` so the cache returns rules for every page; `APPLY_RULES_FOR_PAGE` then
     * subsets by the active route. Passing `page_id` on the catalog fetch would strand SPA navigations with only the first page’s rules.
     * The catalog is refetched when the context changes, when it is older than `RULES_CATALOG_MAX_AGE_MS`, or when `force` is set,
     * so newly published or re-enabled rules are picked up without a reload.
     * Legacy (`psfe_fast_mode` off): per-request GET with `page_id` and direct `SET_RULE_TYPE` commits.
     */
    async fetchRules({commit, state}, {effectiveDate, pageId, transactionType, policyState, policySystemId, rulesFastMode, force = false}) {
        const systemId = policySystemId ?? state.policyVersion.policySystemId;

        if (!rulesFastMode) {
            const response = await authedAxios.get(`/policy-systems/${systemId}/rules/`, {
                    params: {
                        'enabled': true,
                        'page_id': pageId,
                        'state': policyState,
                        'as_of_date': effectiveDate,
                        'transaction_type': transactionType,
                    }
                }),

                rules = deepCase(response?.data?.data || [], 'camel'),
                groupedRules = groupRulesByType(rules);

            commit('SET_RULE_TYPE', {
                rules: groupedRules.cleanUp || [],
                type: 'cleanup',
            });

            commit('SET_RULE_TYPE', {
                rules: groupedRules.pageDisplay || [],
                type: 'pageDisplay',
            });

            commit('SET_RULE_TYPE', {
                rules: groupedRules.pageValidation || [],
                type: 'pageValidation',
            });

            commit('SET_RULE_TYPE', {
                rules: groupedRules.crossValidation || [],
                type: 'crossValidation',
            });

            return rules;
        }

        const ctxKey = [systemId, effectiveDate, transactionType, policyState].join('|'),
            catalogIsStale = Date.now() - state.rulesCatalogFetchedAt >= RULES_CATALOG_MAX_AGE_MS;

        if (force || catalogIsStale || state.rulesCatalogContextKey !== ctxKey) {
            const response = await authedAxios.get(`/policy-systems/${systemId}/rules/`, {
                    params: {
                        'enabled': true,
                        'state': policyState,
                        'as_of_date': effectiveDate,
                        'transaction_type': transactionType,
                    }
                }),

                // Rule versions are fetched from cache, so we cannot deserialize the way we normally would
                rules = deepCase(response?.data?.data || [], 'camel');

            if (!rules.length) {
                commit('SET_RULES_CATALOG', {cleanUp: [], pageDisplay: [], pageValidation: [], crossValidation: []});
                commit('SET_RULES_CATALOG_CONTEXT_KEY', ctxKey);
                commit('SET_RULES_CATALOG_FETCHED_AT', Date.now());
                commit('APPLY_RULES_FOR_PAGE', pageId);

                return rules;
            }

            const groupedRules = groupRulesByType(rules);

            commit('SET_RULES_CATALOG', groupedRules);
            commit('SET_RULES_CATALOG_CONTEXT_KEY', ctxKey);
            commit('SET_RULES_CATALOG_FETCHED_AT', Date.now());
        }

        commit('APPLY_RULES_FOR_PAGE', pageId);

        const cat = state.rulesCatalog;

        return []
            .concat(cat.pageDisplay || [], cat.pageValidation || [], cat.crossValidation || [], cat.cleanUp || []);
    },
    async evaluateRules({state}, {data, signal}) {
        const response = await authedAxios.post(
            `/policy-systems/${state.policySystem.id}/rules/`,
            utils.dataFormatter.serialize({stuff: {type: 'Rule', ...data}}),
            {signal},
        );

        return deepCase(response?.data?.data || [], 'camel');
    },
    async runJob(_, {code, variables}) {
        const response = await authedAxios.post(`${process.env.VUE_APP_BEHANDLE_URL}/api/v1/jobs/`,
            utils.dataFormatter.serialize({
                stuff: {
                    code,
                    type: 'Job',
                    variables: deepCase(variables, 'snake'),
                },
            }),
        );

        return utils.dataFormatter.deserialize(response.data);
    },
    async savePolicyGroup({commit}, data) {
        const response = await authedAxios.patch(`${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/policy-groups/${data.id}/?include=agency,mailing_address,named_insureds.entities`, utils.dataFormatter.serialize({stuff: data})),
            policyGroup = deepCase(utils.dataFormatter.deserialize(response.data), 'camel', ['extension', 'dynamicOptions']);

        commit('SET_POLICY_SYSTEM_DATA', {type: 'policyGroup', data: policyGroup});

        return policyGroup;
    },
    async savePolicyOrVersion({commit}, {data, type}) {
        const routeRoot = type === 'policy' ? 'policies' : 'versions',
            queryParams = type === 'policy' ? '' : `?include=${getPolicyVersionIncludes()}`,
            response = await authedAxios.patch(`${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/${routeRoot}/${data.id}/${queryParams}`, utils.dataFormatter.serialize({stuff: data})),
            // Need to use `deepCase` to serialize nested relations within admin fields
            updatedObject = deepCase(utils.dataFormatter.deserialize(response.data), 'camel', ['extension', 'dynamicOptions']);

        commit('SET_POLICY_SYSTEM_DATA', {type: type === 'policy' ? 'policy' : 'policyVersion', data: updatedObject});

        return updatedObject;
    },
    async savePolicyResource(_, {data, method, type, versionId, query = ''}) {
        let url = `${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/versions/${versionId}/${policyDataUrlType[data.type]}/`,
            includes = policyDataIncludes.types[snakeCase(type)];

        if (method !== 'post') {
            url += `${data.id}/`;
        }

        if (includes) {
            url += `?include=${includes}`;
        }

        const response = await authedAxios[method](`${url}${query}`, utils.dataFormatter.serialize({stuff: data}));

        return utils.dataFormatter.deserialize(response.data);
    },
    async submitProcess(_, {code, versionId}) {
        const response = await authedAxios.post(`${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/versions/${versionId}/submit-process/`,
            utils.dataFormatter.serialize({stuff: {code, type: 'Version'}}));

        return utils.dataFormatter.deserialize(response.data.data);
    },
    async rateVersion(context, { versionId, policyId, ratingIdentifier }) {
        const response = await authedAxios.post(
                `${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/policies/${policyId}/versions/${versionId}/rate/${ratingIdentifier}/?include=${getPolicyVersionIncludes()}`
            ),

            policyVersion = deepCase(utils.dataFormatter.deserialize(response.data), 'camel', ['extension', 'dynamicOptions']);

        return await context.dispatch('setPolicyData', policyVersion);
    },
    async reapplyTransaction(_, versionId) {
        return await authedAxios.patch(`${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/versions/${versionId}/reapply/`);
    },
};

export const mutations = {
    SET_TEST_SET(state, value) {
        state.testSet = value ?? null;
    },
    SET_CROSS_ERRORS(state, crossErrors) {
        state.crossErrors = crossErrors;
        sessionStorage.setItem('crossErrors', JSON.stringify(crossErrors));
    },
    SET_POLICY_SYSTEM_DATA(state, {type, data}) {
        state[type] = data;
    },
    SET_RULE_TYPE(state, {rules, type}) {
        state.rules[type] = rules;
    },
    SET_RULES_CATALOG(state, groupedRules) {
        state.rulesCatalog = {
            cleanUp: groupedRules.cleanUp || [],
            pageDisplay: groupedRules.pageDisplay || [],
            pageValidation: groupedRules.pageValidation || [],
            crossValidation: groupedRules.crossValidation || [],
        };
    },
    SET_RULES_CATALOG_CONTEXT_KEY(state, key) {
        state.rulesCatalogContextKey = key;
    },
    SET_RULES_CATALOG_FETCHED_AT(state, timestamp) {
        state.rulesCatalogFetchedAt = timestamp;
    },
    APPLY_RULES_FOR_PAGE(state, pageId) {
        const cat = state.rulesCatalog,
            appliesToPage = (rule) => !rule.pageId || rule.pageId === pageId;

        state.rules.pageDisplay = (cat.pageDisplay || []).filter(appliesToPage);
        state.rules.pageValidation = (cat.pageValidation || []).filter(appliesToPage);
        state.rules.cleanup = (cat.cleanUp || []).filter(appliesToPage);
        state.rules.crossValidation = cat.crossValidation || [];
    },
    SET_PAGE(state, {pageId, pageType, policySystemId}) {
        // Caching the quote and submission page id by policy system, so they can be navigated to throughout the system when needed
        let cachedPages = JSON.parse(sessionStorage.getItem('policySystemPages')) || {};

        if (!cachedPages[policySystemId]) {
            cachedPages[policySystemId] = {};
        }

        state[`${pageType}PageId`] = pageId;
        cachedPages[policySystemId][`${pageType}PageId`] = pageId;
        sessionStorage.setItem('policySystemPages', JSON.stringify(cachedPages));
    },
};

export const state = () => {
    return {
        crossErrors: JSON.parse(sessionStorage.getItem('crossErrors')) || {underwriterAccommodation: [], agentAccommodation: [], displayOnly: []},
        debugMode: false,
        isPreview: false,
        pageId: '',
        pageVersion: {},
        policy: {},
        policyGroup: {},
        policyVersion: {},
        priorVersion: {},
        policyPrefix: '',
        policySystem: {},
        policySystemLoading: false,
        quotePageId: null,
        submissionPageId: null,
        pageLoading: true,
        rulesCatalog: {
            cleanUp: [],
            pageDisplay: [],
            pageValidation: [],
            crossValidation: [],
        },
        rulesCatalogContextKey: null,
        rulesCatalogFetchedAt: 0,
        rules: {
            byField: {},
            byTargetReferenceName: {},
            cleanup: [],
            pageDisplay: [],
            pageValidation: [],
            crossValidation: [],
        },
        selectedError: {},
        testSet: null,
    };
};

export default {
    state: state(),
    actions,
    getters,
    namespaced: true,
    mutations,
};
